// FleetFlix Domain Configuration
export const fleetflixConfig = {
  // Basic domain information
  id: 'fleet-flix',
  name: 'FleetFlix',
  tagline: 'Fleet Video Management',
  description: 'Comprehensive fleet video management and streaming platform.',
  
  // Assets
  logo: '/assets/logos/fleet-flix-logo',
  whiteLogo: '/assets/logos/fleet-flix-white-logo',
  favicon: '/assets/favicons/fleet-flix-favicon.png',
  icon: '/assets/icons/fleet-flix',
  
  // Navigation
  pages: ['home', 'solutions', 'products', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Products', path: '/products' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
  
  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/fleetflix',
  
  // UI Configuration
  variant: 'landing',
  showFeatures: false,
  showTestimonials: false,
  
  // Content sections
  hero: {
    title: 'FleetFlix',
    subtitle: 'Stream, manage, analyze and coach your entire fleet with our comprehensive camera management platform.',
    features: ['Video Streaming', 'Analytics', 'Fleet Management', 'Driver Coaching']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'FleetFlix Solution Gallery',
    subtitle: 'Explore our comprehensive fleet video management and streaming capabilities',
    description: 'See how FleetFlix transforms fleet operations with advanced video technology'
  },

  features: {
    title: 'FleetFlix Features',
    subtitle: 'AI-powered camera and comprehensive fleet video management platform',
    description: 'Advanced camera management and coaching platform designed specifically for fleet operations.',
    items: [
      {
        title: 'HD Video Visibility + 360° View',
        description: 'Captures high-definition footage of the road with wide-angle coverage and a clear view of the driver and in-cabin activity with a color daytime driver-facing camera.',
        icon: '📹'
      },
      {
        title: 'Infrared (IR) LEDs',
        description: 'Enhances low-light and nighttime performance, ensuring clear video recordings in dark environments.',
        icon: '📡'
      },
      {
        title: 'Driver Alert Button',
        description: 'Enables drivers to manually trigger event recordings for capturing critical moments not automatically detected.',
        icon: '🚨'
      },
      {
        title: 'High-speed 4G LTE Cellular Connectivity',
        description: '10x speed advantage over 3G devices, with a built-in WiFi hotspot for laptops and mobile devices.',
        icon: '📱'
      },
      {
        title: 'Diverse and Flexible Coverage',
        description: 'Engine diagnostics from light to heavy-duty vehicles. Ruggedized for vibration, shock, moisture, and extreme temperatures.',
        icon: '📊'
      },
      {
        title: 'Safety Reporting & InsightsDetect',
        description: 'Leveraging cutting-edge AI Dash Cams, RoadEazy effortlessly records high-definition videos, swiftly transmitting crucial footage of significant incidents to the cloud for prompt review.',
        icon: '📁'
      }
    ]
  },
  
  solutions: {
    title: 'FleetFlix Solutions',
    subtitle: 'All-in-one fleet management platform',
    description: 'Manage, stream, analyze, coach and more with our FleetFlix camera management platform.',
    sections: [
      {
        title: 'Proactively notify employees about potential risks',
        description: 'Set up automated alerts to closely monitor high-risk incidents, enabling safety managers to prioritize critical aspects for your safety program.'
      },
      {
        title: 'Access to reliable safety reporting and resources',
        description: "Access vital safety metrics for board reports, ESG reporting, or investor and analyst relations, along with valuable tools and resources to reduce accident and collision rates for significant social impact.",
      },
      {
        title: 'Coach the riskiest drivers at scale with streamlined workflows',
        description: 'Empower your drivers both on the road and in the office through powerful coaching tools that guarantee punctuality, effectiveness, and accountability. Foster a culture of safe driving by implementing driver-centric gamification, incentivizing responsible behavior, and establishing rewards programs that build strong trust and rapport with your drivers.',
      },
      {
        title: 'Mobile Coaching App for Driver Safety',
        description: 'Our mobile coaching app empowers drivers to access safety insights and improve their driving habits, while enabling safety managers to monitor driver behavior and provide timely coaching.',
      }
    ]
  },
  
  
  testimonials: [
    {
      content: "FleetFlix has been a game-changer for our fleet. It has helped us improve driver safety and efficiency, and has given us valuable insights into our operations.",
      author: "Oscar Zuniga",
      role: "Director of Fleet Safety and Compliance",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/25.jpg"
    },
    {
      content: "We've been using FleetFlix for a few years now, and it has been a great tool for our fleet. FleetFlix continues to evolve and improve, and we're excited to see what's next.",
      author: "Samantha Brown",
      role: "Safety Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/20.jpg"
    }
  ],

  // Products
  products: {
    title: 'Explore our comprehensive range of products',
    subtitle: 'All FleetFlix products are designed to optimize your fleet operations.',
    categories: [
      { id: 'all', name: 'All Products' },
      { id: 'cameras', name: 'Cameras' },
      { id: 'aux', name: 'Auxiliary' },
      { id: 'monitor', name: 'Monitors' },
      { id: 'harnesses', name: 'Harnesses' },
    ],
    items: [
      {
        id: 4,
        title: 'FleetFlix AI+ Pro Camera',
        description: 'AI-powered driver monitoring solution designed to detect distracted driving, drowsiness, and smartphone usage.',
        brand: 'FleetFlix',
        category: 'cameras',
        productLink: 'https://shop.fleethoster.com/products/dcc01-ffproa',
        image: 'product-4',
        featured: true,
        details: [
          {
            title: 'AI-powered driver monitoring solution (DMS)',
            description: 'Dual 1080P front and interior cameras and processes video footage to provide real-time warnings and feedback to drivers.',
            features: [
              'Live View',
              'OBD2 cabling',
              'GPS',
              'Notifications',
              'Full integration with Geotab'
            ]
          },
          {
            title: 'KEY HARDWARE FEATURES',
            description: '',
            features: [
              'Dual 1080P cameras (front and rear)',
              'AI-powered driver monitoring',
              'Removable micro SD card storage (up to 256GB)',
              'Detection of smartphone usage, drowsiness, and fatigue',
              'Supports additional inputs/outputs',
            ]
          },
          {
            title: 'Product Specifications',
            description: '',
            features: [
              'Dimensions - 4.05 in × 1.77 in × 1.37 in (103 mm × 45 mm × 35 mm)',
              'Weight - 2.82 oz (0.176 lbs) (80 g)',
              'AI-powered driver monitoring',
              'Operating Temperature - -20°C to 70°C (-4°F to 158°F)',
              'Storage Temperature - -30°C to 80°C (-22°F to 176°F)',
              'Power Supply - 5V/2A',
              'Storage - Micro SD card (up to 256GB)',
            ]
          }
        ]
      },
      // {
      //   id: 5,
      //   title: 'Fleetflix LITE PRO Dual Dash Camera',
      //   description: 'AI-powered solution designed for fleet management',
      //   brand: 'FleetFlix',
      //   category: 'cameras',
      //   image: 'product-5',
      //   featured: false,
      //   details: [
      //     {
      //       title: 'AI-powered solution',
      //       description: 'The AI-powered solution that not only enhances driver safety but also takes fleet management efficiency to the next level. This advanced dual dash camera is designed to proactively reduce traffic accidents and promote safer driving practices.',
      //       features: []
      //     },
      //     {
      //       title: 'Cutting-edge AI technology',
      //       description: 'FleetFlix LITE PRO+ actively detects risky driving events and unsafe behaviors, delivering real-time reminders to drivers on the road. This not only mitigates potential risks but also acts as a valuable tool for driver training. The camera seamlessly uploads events to the fleet management platform, providing a comprehensive overview for strategic decision-making.',
      //       features: []
      //     },
      //     {
      //       title: 'Product Features',
      //       description: '',
      //       features: [
      //         'Ultra-wide 143° road facing lens, up to 1920p uhd video recording',
      //         'Ultra-wide 170° driver facing lens, up to 1080p hd video recording',
      //         'Up to 1tb of storage, utilizing 2 x 512gb dual-micro sd cards for continuous or backup',
      //         '4-channel io input, 1-channel can and i-channel rs232',
      //         'Built-in adas function, lane departure warning (ldw), forward collision warning (fcw), and headway monitoring warning (hmw)',
      //         'Built-in dms function, supporting detection of unsafe driving behaviors: lens covered, yawning, handheld devices, smoking, distraction, no driver, unfasten seat belt',
      //         'Sleep mode remote wakeup',
      //         'Built-in g-axis gravity sensor support rapid acceleration, rapid deceleration, harsh cornering, accident detection',
      //         'Fully integrated into geotab',
      //       ]
      //     },
      //     {
      //       title: 'Product Specifications',
      //       description: '',
      //       features: [
      //         'Dimensions - 4.44 in × 2.66 in × 3.46 in (113 mm × 68 mm × 88 mm)',
      //         'Weight - 10.4 oz (295 g)',
      //         'Operating Temperature - -40°F to +158°F (-40°C to +70°C)',
      //         'Power Supply - 12V and 24V vehicles',
      //         'Wifi - Supports 2A6',
      //         'Video Recording - Up to 4 Channel',
      //         'Connectivity - LTE FDD B2/B4/B5/B12/B13/B14/B66/B7, WCDMA B2/B4/B5',
      //       ]
      //     }
      //   ]
      // },
      // {
      //   id: 6,
      //   title: 'FleetLix Lite Road Facing Dash Camera',
      //   description: 'Your go-to solution for optimizing fleet management. This cutting-edge dashcam is designed to empower driver privacy and streamline fleet operations, enhancing overall efficiency.',
      //   brand: 'FleetFlix',
      //   category: 'cameras',
      //   image: 'product-6',
      //   featured: false,
      //   details: [
      //     {
      //       title: 'Introducing',
      //       description: 'FleetLix Lite goes beyond traditional dashcams, offering real-time and precise vehicle position information along with comprehensive operational data transmitted directly to the fleet management platform. Stay in control with instant access to crucial insights, allowing for informed decision-making and optimal resource allocation.',
      //       features: []
      //     },
      //     {
      //       title: 'Live Streaming',
      //       description: 'FleetLix Lite with high-quality live streaming functionality, FleetLix Lite fosters seamless communication between drivers and the fleet management team. Experience the convenience of video live view playback, making it easier than ever to review and analyze events on the road.',
      //       features: []
      //     },
      //     {
      //       title: 'Product Features',
      //       description: '',
      //       features: [
      //         'Ultra-wide 143° road facing lens, up to 1920p uhd video recording',
      //         'Up to 1tb of storage, utilizing 2 x 512gb dual-micro sd cards for continuous or backup',
      //         '2 channel video recording',
      //         '4-channel IO input, 1-channel CAN and 1-channel RS232',
      //         'Built-in Wi-Fi, 4G communication module, and inertial navigation positioning module',
      //         'Compact design that doesn\'t affect the driver\'s sight',
      //         'OBD plug-in play installation',
      //         'Sleep mode remote wakeup',
      //         'Supports rapid acceleration, rapid deceleration, harsh cornering, and accident detection',
      //         'Fully integrated into geotab',
      //       ]
      //     },
      //     {
      //       title: 'Product Specifications',
      //       description: '',
      //       features: [
      //         'Dimensions - 4.4 in x 2.66 in x 2.24 in (113.0 mm x 67.8 mm x 57 mm)',
      //         'Weight - 0.584 lbs (265 g)',
      //         'Operating Temperature - -40°F to +158°F (-40°C to +70°C)',
      //         'Power Supply - 12V and 24V Vehicles',
      //         'WiFi - Supports 2.4G',
      //         'IP Rating - IP30',
      //         'Connectivity - 4G LTE FDD: B2/B4/B5/B12/B13/B14/B66/B71, WCDMA: B2/B4/B5'
      //       ]
      //     }
      //   ]
      // },
      {
        id: 9,
        title: 'DCA01-FFLITE-RC-AHD Aux Cam',
        description: 'FleetFlix Lite auxiliary cameras that connect to FleetFlix Lite and FleetFlix Lite Pro+ dashcams, providing 360° video coverage with HD recording from multiple vehicle perspectives.',
        brand: 'FleetFlix',
        category: 'aux',
        productLink: 'https://shop.fleethoster.com/products/dca01-fflite-rc-ahd',
        image: 'product-9',
        featured: false,
        details: [
          {
            title: 'Product Description',
            description: 'FleetFlix Lite auxiliary cameras are connected to FleetFlix Lite and FleetFlix Lite Pro+ dashcams. When connected to the dash camera, the cameras enable live video and recording from additional perspectives during trips. The cameras are installed around the vehicle in key locations (side and rear). All auxiliary cameras include a high-definition 1920×1080 lens, auto gain, 3D noise reduction, and backlight compensation. They provide 360° video coverage for the fleet.',
            features: []
          },
          {
            title: 'Key Features',
            description: '',
            features: [
              'Image sensor: 1/2.8" 2.13M pixel CMOS',
              'Resolution: 1920H × 1080V',
              'Minimum illumination: 0LUX (IR on); 0.05LUX (IR off)',
              'Auto electronic shutter, auto exposure, and auto white balance',
              'IR range: 10M (12 Q5 850NM LED dots)',
              'IP rating: IP67',
              'Includes a 0.5M waterproof aviation cable, DC12V black shell',
              'White balance auto tracking'
            ]
          },
          {
            title: 'Technical Specifications',
            description: '',
            features: [
              'Signal-to-noise ratio: ≥50dB',
              'Electronic shutter speed: 1/25s ~ 1/25,000s (auto)',
              'Day/night mode: ICR IR filter',
              'Operating temperature: -40°C to +70°C',
              'Operating humidity: <95% (no condensation)',
              'Power supply: DC12V ±10%',
              'Dimensions: 3 in × 2.5 in × 2.9 in (75mm × 65.1mm × 73.7mm)',
              'Weight: 0.6 lbs (280g)',
              'Power consumption: ~130mA / DC12V ±5%'
            ]
          },
          {
            title: 'Lens Options',
            description: '',
            features: [
              '2.5mm M12@ F2.0; HFOV: 123°, VFOV: 69°, DFOV: 141°',
              '2.8mm M12@ F2.0; HFOV: 103°, VFOV: 56°, DFOV: 121°',
              '3.6mm M12@ F2.2; HFOV: 88°, VFOV: 49°, DFOV: 103°',
              'Other lens options with varying fields of view available',
              'Composite video output at various specifications'
            ]
          }
        ]
      },
      {
        id: 10,
        title: 'DCA01-FFLITE-SC-CA39-AHD Aux Cam',
        description: 'High-definition auxiliary camera compatible with FleetFlix Lite systems, offering 360° video coverage with advanced features like auto gain and 3D noise reduction.',
        brand: 'FleetFlix',
        category: 'aux',
        productLink: 'https://shop.fleethoster.com/products/dca01-lite-sc-ahd',
        image: 'product-10',
        featured: false,
        details: [
          {
            title: 'Product Description', 
            description: 'FleetFLIX Lite auxiliary cameras are connected to FleetFLIX Lite and FleetFLIX Lite Pro+ dashcams. When connected to the dash camera, the cameras enable live video and recording from the portal of additional perspectives during trips. The cameras are installed around the vehicle in key locations (side and rear). All auxiliary cameras include a high-definition 1920×1080V lens. Auto gain, 3D noise reduction, and backlight compensation are all supported. Provide your fleet with 360° video coverage.',
            features: []
          },
          {
            title: 'Key Features',
            description: '',
            features: [
              'Image sensor: 1/2.8" 2.13M pixel CMOS',
              'Resolution: 1920H × 1080V',
              'Minimum illumination: 0LUX (IR on), 0.05LUX (IR off)', 
              'Auto electronic shutter, auto exposure, and auto white balance',
              'IR range: 10–15M',
              'IP rating: IP69K',
              'Compact design and simple installation',
              'Vehicle damping performance optimization'
            ]
          },
          {
            title: 'Technical Specifications',
            description: '',
            features: [
              'Signal-to-noise ratio: ≥50dB',
              'Electronic shutter: 1/25s ~ 1/25,000s (auto)',
              'Day/night mode: ICR IR filter',
              'Operating temperature: -40°C to +70°C',
              'Operating humidity: <95% (no condensation)',
              'Power supply: DC12V ±10%',
              'Video output: 1.0VP-P composite, 75Ω, 4PIN aviation cable',
              'Power consumption: 260mA / DC12V ±5%',
              'Dimensions: 2.59 in × 2.29 in × 1.8 in (66mm × 58mm × 46mm)',
              'Weight: 0.39 lbs (180g)'
            ]
          },
          {
            title: 'Lens Options',
            description: '',
            features: [
              '2.2mm M12@ F2.0 (HFOV: 152°, VFOV: 83°, DFOV: 180°)',
              '2.8mm M12@ F2.0 (HFOV: 103°, VFOV: 56°, DFOV: 121°)',
              '3.6mm M12@ F2.2 (HFOV: 88°, VFOV: 49°, DFOV: 103°)'
            ]
          }
        ]
      },
      {
        id: 22,
        title: 'DCA01-SC Auxiliary Side Camera',
        description: 'An auxiliary side camera designed to enhance fleet security and visibility with HD resolution and night vision capabilities.',
        brand: 'FleetFlix',
        category: 'aux',
        productLink: 'https://shop.fleethoster.com/products/dca01-sc',
        image: 'product-22',
        featured: false,
        details: [
          {
            title: 'Product Description',
            description: 'The DCA01-SC is an auxiliary side camera designed to enhance fleet security and visibility. This camera is ideal for capturing side views of vehicles, improving situational awareness and safety during fleet operations.',
            features: [
              '1080p HD resolution',
              'Night vision capabilities',
              'Rugged design',
              'Compatibility with FleetFlix AI+ and SurfSight AI-12 systems'
            ]
          },
          {
            title: 'Hardware Features',
            description: '',
            features: [
              'IP66-rated durability for outdoor use',
              'Expandable coverage with support for up to 4 auxiliary cameras',
              'Optional magnetic mounts for easy installation',
              'Customizable event triggers for specific scenarios',
              'Automated event uploads to a server',
              'Seamless integration into Geotab'
            ]
          },
          {
            title: 'Technical Specifications',
            description: '',
            features: [
              'Tech Available: Live stream, Dual IR, 1080p, Nighttime view',
              'Video Availability: Live stream, Event-based',
              'Input Voltage: 12V, 2 amp',
              'Operating Temperature: −10°C to +85°C',
              'Installation Methods: Hardwire powered',
              'Video: Road capture in HD 1080p',
              'IP Rating: IP66 (dust-tight and protected against powerful water jets)'
            ]
          }
        ]
      },
      {
        id: 23,
        title: '7-inch HD Fleet Monitor',
        description: 'High-definition 7-inch monitor designed for fleet vehicles, featuring dual-channel support, IPS display, and rugged construction for enhanced driver visibility and safety.',
        brand: 'FleetFlix',
        category: 'monitor',
        productLink: 'https://shop.fleethoster.com/products/acc01-lite-mon-7hd',
        image: 'product-23',
        featured: true,
        details: [
          {
            title: 'Product Description',
            description: 'The 7-inch high-definition monitor enhances the driver\'s ability to observe vehicle operations and the surrounding environment clearly, improving both safety and efficiency. With simple button operation, high resolution and brightness, high contrast, and wide viewing angle, this monitor is suitable for various driving scenarios, improving the driving experience and vehicle operating efficiency.',
            features: []
          },
          {
            title: 'Features',
            description: '',
            features: [
              'Dual-channel support with 2 video inputs and dual/single image segmentation display',
              'IPS screen with fast response speed, wide viewing angle, and true color (1024 RGB × 600 resolution)',
              'OSD menu for mirror/normal image settings, delay time configuration, and trigger priority management',
              'Rugged design with high-temperature resistant ABS + PC materials',
              'Removable sun hat to reduce glare from direct sunlight',
              'Rust-proof metal bracket with U-shaped double-sided fastening and anti-vibration design',
              'High brightness of 600 cd/㎡ for clear image visibility',
              'Load dump pulse voltage protection (DC 24V + 174V for 350 ms)',
              'Energy-efficient power supply with low-temperature stability',
              'Wide voltage range operation (DC 11–32V)',
              'Built-in reverse polarity protection with LED thermal response'
            ]
          },
          {
            title: 'Technical Specifications',
            description: '',
            features: [
              'Screen Size: 7 inches (177 mm)',
              'Color System: AHD 720P / 1080P',
              'Display Resolution: 1024 RGB(H) × 600(V)',
              'Viewable Area: 154.4 mm × 85.9 mm',
              'Brightness: 600 cd/㎡',
              'Contrast Ratio: 700:1',
              'Viewing Angle: 160° / 160°',
              'LED Power: 8.7V / 220mA',
              'Maximum Power Consumption: 5W',
              'Operating Voltage: DC 11–32V',
              'Operating Temperature: −20°C to +70°C (−4°F to +158°F)',
              'Storage Temperature: −30°C to +80°C (−30°F to +176°F)',
              'Motherboard Chipset: ATM630M',
              'Power Output: DC 10.5V / 750mA',
              'Dimensions: 8.5 in × 5.9 in × 3.3 in (216 mm × 150 mm × 85 mm)',
              'Net Weight: 28.2 oz (800 g)'
            ]
          }
        ]
      },
      {
        id: 12,
        title: 'Harness 3WOBDK',
        description: '',
        brand: 'FleetFlix',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-3wobdk',
        image: 'product-12',
        featured: false
      },
      // {
      //   id: 13,
      //   title: 'Harness 4W6POBDK',
      //   description: '',
      //   brand: 'FleetFlix',
      //   category: 'harnesses',
      //   productLink: 'https://shop.fleethoster.com/products/hrn01-4w6pobdk',
      //   image: 'product-13',
      //   featured: false
      // },
      {
        id: 14,
        title: 'Harness 4W9POBDK',
        description: '',
        brand: 'FleetFlix',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-4w9pobdk',
        image: 'product-14',
        featured: false
      },
      {
        id: 15,
        title: 'Harness 4WOBDK',
        description: '',
        brand: 'FleetFlix',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-4wobdk',
        image: 'product-15',
        featured: false
      },
    ]
  },

  blog: {
    title: 'FleetFlix Blog',
    subtitle: '',
    description: 'Latest technology news and updates in fleet management and camera systems.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'FleetFlix - Fleet Camera Management & Streaming Platform',
    description: 'Comprehensive fleet camera platform to stream, manage, analyze, and coach your entire fleet efficiently.',
    keywords: [
      'fleet video', 'video management', 'fleet streaming', 'video analytics', 'fleet platform',
      'fleet video streaming', 'video platform', 'fleet media', 'video distribution', 'fleet content',
      'fleet video analytics', 'video streaming', 'fleet media platform', 'video management system',
      'fleet video solutions', 'streaming platform', 'fleet video technology', 'video management tools'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'FleetFlix - Comprehensive Fleet Camera Management Platform',
    ogDescription: 'Comprehensive fleet video management and streaming platform. Stream, manage, and analyze fleet videos.',
    ogType: 'website',
    ogUrl: 'https://www.fleetflix.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'FleetFlix - Comprehensive Fleet Camera Management Platform',
    twitterDescription: 'Comprehensive fleet video management and streaming platform. Stream, manage, and analyze fleet videos.',
    
    // Robots
    robots: 'index, follow',
    author: 'FleetFlix',
    publisher: 'FleetFlix',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'FleetFlix - AI-powered camera and fleet camera management platform',
        description: 'Comprehensive fleet camera management and streaming platform. Stream, manage, analyze and coach fleet videos.',
        keywords: ['fleet video', 'video management', 'fleet streaming', 'video analytics', 'fleet platform']
      },
      solutions: {
        title: 'FleetFlix Solutions - Professional Fleet Camera Management',
        description: 'Comprehensive fleet camera management and streaming services with professional video analytics and distribution tools.',
        keywords: ['fleet video solutions', 'video management services', 'streaming solutions', 'video analytics']
      },
      products: {
        title: 'FleetFlix Products - Fleet Camera Management Tools & Software',
        description: 'Professional fleet camera management tools and software. Video streaming, analytics, and distribution platforms.',
        keywords: ['fleet video tools', 'video management software', 'streaming tools', 'video analytics']
      },
      blog: {
        title: 'FleetFlix News & Insights - Fleet Camera Management Blog',
        description: 'Get the latest news, insights, and best practices for fleet camera management and streaming, with professional tips and updates.',
        keywords: ['fleet video news', 'video management insights', 'streaming best practices', 'fleet media tips']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
